/**
 * Authentication Service
 * Handles user authentication (replaces Firebase Auth)
 */

import { authAPI, saveAuthToken, removeAuthToken, RegisterData, LoginData } from './apiClient';

export interface User {
  id: string;
  email: string;
  display_name?: string;
  phone_number?: string;
  role: string;
  email_verified: boolean;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
}

/**
 * Register new user
 */
export const register = async (data: RegisterData): Promise<AuthState> => {
  try {
    const response = await authAPI.register(data);
    
    if (response.data.success) {
      const { user, token } = response.data.data;
      
      // Save token to localStorage
      saveAuthToken(token);
      
      // Save user data
      localStorage.setItem('user', JSON.stringify(user));
      
      return {
        user,
        token,
        isAuthenticated: true
      };
    }
    
    throw new Error('Registration failed');
  } catch (error: any) {
    throw error.response?.data?.error || 'Registration failed';
  }
};

/**
 * Login user
 */
export const login = async (data: LoginData): Promise<AuthState> => {
  try {
    const response = await authAPI.login(data);
    
    if (response.data.success) {
      const { user, token, companies } = response.data.data;
      
      // Save token to localStorage and cookie
      const refreshToken = response.data.data.refreshToken;
      saveAuthToken(token, refreshToken);
      
      // Save user data
      localStorage.setItem('user', JSON.stringify(user));
      
      // Save companies if available
      if (companies && companies.length > 0) {
        localStorage.setItem('companies', JSON.stringify(companies));
        localStorage.setItem('selectedCompany', companies[0].id);
      }
      
      // Clear any previous role selections on new login
      localStorage.removeItem('selectedRole');
      localStorage.removeItem('selectedPersonnelId');
      localStorage.removeItem('selectedUserId');
      
      return {
        user,
        token,
        isAuthenticated: true
      };
    }
    
    throw new Error('Login failed');
  } catch (error: any) {
    throw error.response?.data?.error || 'Login failed';
  }
};

/**
 * Logout user - Complete cleanup
 */
export const logout = async (): Promise<void> => {
  try {
    await authAPI.logout();
  } catch (error) {
  } finally {
    // Clear all auth-related data
    removeAuthToken();
    
    // Clear additional role-related data
    localStorage.removeItem('selectedRole');
    localStorage.removeItem('selectedPersonnelId');
    localStorage.removeItem('selectedUserId');
    localStorage.removeItem('companies');
    localStorage.removeItem('selectedCompany');
    localStorage.removeItem('refreshToken');
    
    // Clear any cached data that might be user-specific
    // Note: We keep cache_* items as they might be useful for performance
    // but clear auth-specific caches
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('auth') ||
        key.startsWith('user') ||
        key.startsWith('company') ||
        key === 'selectedRole' ||
        key === 'selectedPersonnelId' ||
        key === 'selectedUserId'
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }
};

/**
 * Get current user from localStorage
 */
export const getCurrentUser = (): User | null => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('authToken');
  const user = getCurrentUser();
  return !!(token && user);
};

/**
 * Get auth token
 */
export const getToken = (): string | null => {
  return localStorage.getItem('authToken');
};

/**
 * Refresh user data from server
 */
export const refreshUser = async (): Promise<User> => {
  try {
    const response = await authAPI.getCurrentUser();
    const user = response.data.data;
    
    // Update localStorage
    localStorage.setItem('user', JSON.stringify(user));
    
    return user;
  } catch (error) {
    throw error;
  }
};

